import { useEffect, useId, useRef, useState, type InputHTMLAttributes } from 'react'
import { useMeasurements } from '../context/MeasurementContext'
import { convertMeasurement, measurementUnit, unitSymbol, type MeasurementQuantity, type MeasurementUnit } from '../lib/measurement'
import { changeMeasurementDraftUnit, commitMeasurementDraft, createMeasurementDraft, editMeasurementDraft, type MeasurementLimits } from '../lib/measurementDraft'
import { Calculator, Info, PencilLine } from 'lucide-react'

export interface MeasurementInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'onInvalid' | 'min' | 'max' | 'step' | 'required' | 'inputMode'>, MeasurementLimits {
  label: string
  quantity: MeasurementQuantity
  /** Unit of value, onValueChange, min and max. Never inferred from the display profile. */
  storageUnit: MeasurementUnit
  value: number | null
  onValueChange: (value: number | null) => void
  onValidityChange?: (valid: boolean) => void
  compact?: boolean
  unitPlacement?: 'label' | 'suffix'
  labelClassName?: string
  variant?: 'default' | 'indicator'
  showEditIcon?: boolean
  /** Optional inline disclosure, placed before the field label. */
  info?: string
  infoLabel?: string
  /** Native hover hint, also exposed as the input's accessible description. */
  hint?: string
  /** Fixed display precision for computed read-only values; editable drafts keep their source precision. */
  readOnlyFractionDigits?: number
}

export function MeasurementInput({ label, quantity, storageUnit, value: sourceValue, onValueChange, onValidityChange,
  nullable = true, min, max, id, disabled, readOnly, compact = false, unitPlacement = 'label', labelClassName, variant = 'default', showEditIcon = true, info, infoLabel, hint, readOnlyFractionDigits, ...inputProps }: MeasurementInputProps) {
  const { system, locale } = useMeasurements()
  // Decimal database columns may arrive as numeric strings at runtime.
  const value = sourceValue == null ? null : Number(sourceValue)
  const unit = measurementUnit(system, quantity)
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`
  const infoId = `${inputId}-info`
  const [infoOpen, setInfoOpen] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const reportedValidity = useRef<boolean>()
  const [draft, setDraft] = useState(() => createMeasurementDraft(value, storageUnit, unit, locale))
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    setDraft(previous => previous.baseline !== value || previous.storageUnit !== storageUnit
      ? createMeasurementDraft(value, storageUnit, unit, locale)
      : changeMeasurementDraftUnit(previous, unit, locale))
  }, [value, storageUnit, unit, locale])

  const validation = commitMeasurementDraft(draft, { nullable, min, max })
  const error = validation.ok ? '' : validation.error
  useEffect(() => {
    ref.current?.setCustomValidity(disabled || readOnly ? '' : error)
    const valid = !!(disabled || readOnly || !error)
    // Inline parent callbacks may change on every render: notify only on a transition.
    if (!onValidityChange) reportedValidity.current = undefined
    else if (reportedValidity.current !== valid) {
      reportedValidity.current = valid
      onValidityChange(valid)
    }
  }, [error, disabled, readOnly, onValidityChange])

  function commit() {
    if (disabled || readOnly) return true
    setTouched(true)
    const result = commitMeasurementDraft(draft, { nullable, min, max })
    if (!result.ok) return false
    if (result.changed) onValueChange(result.value)
    setDraft(createMeasurementDraft(result.value, storageUnit, unit, locale))
    return true
  }

  const describedBy = [inputProps['aria-describedby'], hint ? hintId : null, info ? infoId : null, touched && error ? errorId : null].filter(Boolean).join(' ') || undefined
  const indicator = variant === 'indicator'
  const editableIndicator = indicator && !disabled && !readOnly
  const displayedText = readOnly && value != null && readOnlyFractionDigits != null
    ? new Intl.NumberFormat(locale, { useGrouping: false, minimumFractionDigits: readOnlyFractionDigits, maximumFractionDigits: readOnlyFractionDigits }).format(convertMeasurement(value, storageUnit, draft.displayUnit))
    : draft.text
  const fieldLabel = <label htmlFor={inputId} className={compact ? 'sr-only' : labelClassName ?? 'block text-xs font-medium mb-1.5 text-slate-600 dark:text-slate-300'}>{unitPlacement === 'suffix' ? <>{label}<span className="sr-only"> ({unitSymbol(draft.displayUnit)})</span></> : `${label} (${unitSymbol(draft.displayUnit)})`}</label>
  return <div data-measurement-appearance={indicator ? 'indicator' : undefined} title={hint}
    className={indicator ? `group/measurement relative w-full rounded-xl border bg-white/30 p-3 transition-colors dark:bg-slate-800/25 ${editableIndicator ? 'border-white/70 hover:border-orange-200 hover:bg-orange-50/35 focus-within:border-orange-400 focus-within:bg-white/60 focus-within:ring-2 focus-within:ring-orange-500/15 dark:hover:border-orange-700 dark:focus-within:bg-slate-800/60' : 'border-white/40 dark:border-slate-700/50'}` : 'relative w-full'}>
    {info ? <>
      <div data-field-info-label className="flex items-start gap-1">
        <button type="button" aria-label={infoLabel ?? `Informations sur ${label}`} aria-expanded={infoOpen} aria-controls={infoId}
          onClick={() => setInfoOpen(open => !open)} onKeyDown={event => { if (event.key === 'Escape' && infoOpen) { event.preventDefault(); event.stopPropagation(); setInfoOpen(false) } }}
          className="-ml-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-orange-800 outline-none hover:bg-orange-100/70 focus-visible:ring-2 focus-visible:ring-orange-500 dark:text-orange-300 dark:hover:bg-orange-950/50"><Info aria-hidden="true" size={14} /></button>
        {fieldLabel}
      </div>
      <p id={infoId} hidden={!infoOpen} className="mb-2 rounded-lg border border-orange-200/60 bg-orange-50/60 p-2 text-xs leading-5 text-slate-600 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-slate-300">{info}</p>
    </> : fieldLabel}
    {editableIndicator && showEditIcon && <PencilLine aria-hidden="true" size={12} className="pointer-events-none absolute right-3 top-3.5 text-slate-400 transition-colors group-hover/measurement:text-orange-500 group-focus-within/measurement:text-orange-500" />}
    {indicator && readOnly && <Calculator aria-hidden="true" size={12} className="pointer-events-none absolute right-3 top-3.5 text-slate-400" />}
    <div className="relative">
    <input data-measurement-input data-measurement-dirty={draft.dirty} {...inputProps} id={inputId} ref={ref} type="text" inputMode="decimal"
      disabled={disabled} readOnly={readOnly} required={!nullable}
      title={inputProps.title ?? hint ?? (editableIndicator ? `Modifier ${label.toLocaleLowerCase('fr-FR')}` : undefined)}
      className={`${inputProps.className ?? ''}${unitPlacement === 'suffix' ? indicator ? ' pr-8' : ' pr-14' : compact ? ' pr-8' : ''}`}
      value={displayedText} aria-invalid={!!error} aria-describedby={describedBy}
      onChange={event => setDraft(previous => editMeasurementDraft(previous, event.target.value))}
      onBlur={commit}
      onInvalid={() => setTouched(true)}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault()
          setDraft(createMeasurementDraft(value, storageUnit, unit, locale))
          setTouched(false)
        } else if (event.key === 'Enter' && !commit()) event.preventDefault()
      }} />
    {unitPlacement === 'suffix' ? <span aria-hidden="true" className={`pointer-events-none absolute inset-y-0 right-0 flex items-center font-medium ${indicator ? 'text-[11px] text-slate-400' : 'px-3 text-xs text-slate-500'}`}>{unitSymbol(draft.displayUnit)}</span>
      : compact && <span aria-hidden="true" className="absolute right-1.5 top-2 text-[10px] text-slate-500 pointer-events-none">{unitSymbol(draft.displayUnit)}</span>}
    </div>
    {hint && <span id={hintId} className="sr-only">{hint}</span>}
    {touched && error && <span id={errorId} role="alert">{error}</span>}
    {draft.displayUnit !== unit && <small>Terminez la saisie en {unitSymbol(draft.displayUnit)} pour appliquer le changement d’unité.</small>}
  </div>
}
